import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileCsv, faFileExcel, faFilePdf, faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import { api, ApiError } from '../../lib/api/client';
import { useIsSiteAdmin } from '../../lib/api/hooks';
import { toCsv } from '../../lib/reports/csv';
import { toXlsx } from '../../lib/reports/xlsx';
import { downloadBlob, printDocument } from '../../lib/reports/download';
import {
  DOCE_TXN_COLUMNS,
  doceTxnReportHtml,
  money,
  rowNote,
  shortDate,
  subjectName,
  txnReportFilename,
  type DoceTxnReport,
} from '../../lib/reports/doceTransactions';

/**
 * Reconciling the FBI channeler's invoice.
 *
 * The channeler bills per submission they process. Two things in our records are not that, and both come
 * off the count: a submission the router never sent, and a CORRECTION of one that errored — we were
 * charged for the submission being corrected. A RESUBMIT is a third thing and is never its own row,
 * because `POST /doce/resubmit` re-sends the same TCN; the send count is shown beside the row instead, so
 * an invoice counting files rather than transactions shows up as a difference rather than as an
 * unexplained overcharge.
 *
 * The rules are in `services/lib/domain/doceBilling.ts`, with the reasoning and the tests.
 */

const XLSX_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/** The month just gone, which is the one an invoice arrives for. */
function lastMonth(): { from: string; to: string } {
  const now = new Date();
  const first = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1));
  const last = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 0));
  const iso = (d: Date) => d.toISOString().slice(0, 10);
  return { from: iso(first), to: iso(last) };
}

export function DoceTransactions() {
  const isAdmin = useIsSiteAdmin();
  const initial = lastMonth();
  const [from, setFrom] = useState(initial.from);
  const [to, setTo] = useState(initial.to);
  /*
   * The range that was RUN, not the range in the inputs.
   *
   * Typing a date must not fire a query per keystroke, and — more to the point — the figures on screen
   * have to belong to the range named above them. Re-querying as the field changed would briefly show
   * August's total under September's heading, which on a reconciliation page is worse than slow.
   */
  const [ran, setRan] = useState<{ from: string; to: string } | null>(null);
  const [blocked, setBlocked] = useState(false);

  const report = useQuery({
    queryKey: ['admin-doce-transactions', ran?.from, ran?.to],
    queryFn: () =>
      api.get<DoceTxnReport>(`/admin/reports/doce-transactions?from=${ran!.from}&to=${ran!.to}`),
    enabled: isAdmin && ran !== null,
    staleTime: 5 * 60_000,
  });

  const data = report.data;
  const rows = useMemo(() => data?.rows ?? [], [data]);
  const filename = data ? txnReportFilename(data.from, data.to) : 'doce-transactions';

  const exportCsv = () => downloadBlob(toCsv(rows, DOCE_TXN_COLUMNS), `${filename}.csv`, 'text/csv;charset=utf-8');
  const exportXlsx = () =>
    downloadBlob(toXlsx(rows, DOCE_TXN_COLUMNS, { sheetName: 'DOCE transactions' }), `${filename}.xlsx`, XLSX_TYPE);
  const exportPdf = () => data && setBlocked(!printDocument(doceTxnReportHtml(data)));

  if (!isAdmin) {
    return (
      <div className="panel empty">
        <h3>Restricted</h3>
        <p>This report is available to Lakota platform staff only.</p>
      </div>
    );
  }

  const rangeValid = from <= to;
  const billing = data?.billing;

  return (
    <div>
      <div className="console-head">
        <div>
          <h1>DOCE transactions</h1>
          <div className="sub">
            Every FBI Departmental Order submission in a date range, with what the channeler should be
            charging for it.
          </div>
        </div>
        <div className="rpt-actions">
          <button className="btn btn-sm" onClick={exportPdf} disabled={!rows.length}>
            <FontAwesomeIcon icon={faFilePdf} /> PDF
          </button>
          <button className="btn btn-sm" onClick={exportXlsx} disabled={!rows.length}>
            <FontAwesomeIcon icon={faFileExcel} /> Excel
          </button>
          <button className="btn btn-sm" onClick={exportCsv} disabled={!rows.length}>
            <FontAwesomeIcon icon={faFileCsv} /> CSV
          </button>
        </div>
      </div>

      <div className="panel rpt-controls">
        <label className="rpt-date">
          From <input type="date" className="input" value={from} onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label className="rpt-date">
          To <input type="date" className="input" value={to} onChange={(e) => setTo(e.target.value)} />
        </label>
        <button className="btn btn-sm" onClick={() => setRan({ from, to })} disabled={!rangeValid || report.isFetching}>
          <FontAwesomeIcon icon={faMagnifyingGlass} /> {report.isFetching ? 'Reading…' : 'Run report'}
        </button>
        {!rangeValid && <span className="rpt-warn">The start date is after the end date.</span>}
      </div>

      {blocked && <p className="cart-error">Allow pop-ups for this site to print the report.</p>}

      {!ran ? (
        <div className="panel empty">
          <h3>Pick a range</h3>
          <p>Choose the period the invoice covers and run the report.</p>
        </div>
      ) : report.isPending ? (
        <p className="loading-screen">// reading submissions…</p>
      ) : report.isError ? (
        <div className="panel empty">
          <h3>Could not build the report</h3>
          <p>{report.error instanceof ApiError ? report.error.message : 'Try again in a moment.'}</p>
        </div>
      ) : (
        <>
          {/* The totals first: the question this page answers is "does the invoice match". */}
          <div className="panel">
            <h3 className="rpt-h">Expected invoice</h3>
            <table className="tx-table">
              <thead>
                <tr>
                  <th>Submitter</th>
                  <th className="num">Billable</th>
                  <th className="num">Rate</th>
                  <th className="num">Amount</th>
                  <th className="num">Excluded</th>
                  <th className="num">Extra sends</th>
                </tr>
              </thead>
              <tbody>
                {billing?.bySubmitter.map((l) => (
                  <tr key={l.submitterId}>
                    <td>
                      <div className="rpt-cust">{l.label}</div>
                      <div className="rpt-dim mono">{l.submitterId}</div>
                    </td>
                    <td className="num">{l.billable}</td>
                    <td className="num">{money(l.rateCents)}</td>
                    <td className="num"><strong>{money(l.amountCents)}</strong></td>
                    <td className="num">{l.excluded || <span className="rpt-none">—</span>}</td>
                    <td className="num">{l.extraAttempts || <span className="rpt-none">—</span>}</td>
                  </tr>
                ))}
                {!billing?.bySubmitter.length && (
                  <tr><td colSpan={6} className="rpt-dim">No DOCE submissions in this range.</td></tr>
                )}
                <tr className="rpt-total">
                  <td><strong>Total</strong></td>
                  <td className="num"><strong>{billing?.totalBillable ?? 0}</strong></td>
                  <td />
                  <td className="num"><strong>{money(billing?.totalCents ?? 0)}</strong></td>
                  <td className="num">{billing?.totalExcluded || <span className="rpt-none">—</span>}</td>
                  <td className="num">{billing?.totalExtraAttempts || <span className="rpt-none">—</span>}</td>
                </tr>
              </tbody>
            </table>
            <p className="rpt-dim rpt-explain">
              A <strong>resubmission</strong> is the same TCN sent again and is never a second line — the
              send count is shown instead. A <strong>correction</strong> is a new TCN replacing one that
              errored, and is excluded because the submission it replaces was already charged. A submission
              the router never sent is excluded because nobody processed it.
            </p>
          </div>

          <h3 className="rpt-h rpt-h-spaced">
            Submissions <span className="rpt-dim">({rows.length} across {data?.accountsSearched} DOCE accounts)</span>
          </h3>
          <div className="panel" style={{ padding: 0, overflow: 'hidden' }}>
            <table className="tx-table">
              <thead>
                <tr>
                  <th>Date</th><th>Customer</th><th>TCN</th><th>Subject</th><th>State</th>
                  <th className="num">Sends</th><th className="num">Charge</th><th>Note</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  // Shown, not hidden: an excluded row is the evidence for the number being disputed.
                  <tr key={r.transactionId} className={r.billable ? undefined : 'rpt-excluded'}>
                    <td>{shortDate(r.createdOn)}</td>
                    <td>{r.accountName}</td>
                    <td className="mono rpt-tcn">{r.tcn}</td>
                    <td>{subjectName(r)}</td>
                    <td>{r.state}</td>
                    <td className="num">{r.attempts > 1 ? r.attempts : <span className="rpt-none">—</span>}</td>
                    <td className="num">{money(r.billable ? r.rateCents : 0)}</td>
                    <td className="rpt-dim">{rowNote(r)}</td>
                  </tr>
                ))}
                {!rows.length && (
                  <tr><td colSpan={8} className="rpt-dim">Nothing in this range.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
